import type { ForwardedRef, PropsWithChildren } from 'react';
import { createContext, forwardRef, useContext, useMemo } from 'react';

import type { ColorName } from '@hivebrite/design-system/Theme';

import type { Hivebrite } from 'hb-builder/types';
import type { WorkspaceConfig } from 'hb-react/backoffice/components/PageBuilder/types/workspaceConfig';

type RendererContextType = RendererProviderProps & {
  ref: ForwardedRef<HTMLElement>;
};

const RendererContext = createContext<RendererContextType | undefined>(
  undefined,
);

export type RendererProviderProps = Pick<
  Hivebrite,
  'ids' | 'networkFont' | 'networkRootUrl' | 'subdomainNetworkUrl'
> &
  PropsWithChildren<{
    bodyWidth: number | string;
    isMobilePreview: boolean;
    isPublic: boolean;
    mockData?: boolean;
    scaleRatio?: number;
    whiteLabelUrl?: string | null;
    workspaceConfig: WorkspaceConfig;
    textColor?: ColorName;
    fontFamily?: string;
  }>;

export const RendererProvider = forwardRef<
  HTMLElement,
  PropsWithChildren<RendererProviderProps>
>(({ children, scaleRatio = 1, ...otherProps }, ref) => {
  const value: RendererContextType = useMemo(
    () => ({ ...otherProps, scaleRatio, ref }),
    [otherProps, scaleRatio, ref],
  );

  return (
    <RendererContext.Provider value={value}>
      {children}
    </RendererContext.Provider>
  );
});

export const useRendererContext = () => {
  const context = useContext(RendererContext) as RendererContextType;

  if (!context) {
    throw new Error(
      'useRendererContext must be used within a RendererProvider',
    );
  }

  return context;
};

export default RendererContext;
